import { useNavigate, useLocation } from "react-router-dom";
import MainLogo from "../../assets/images/AstraLogo.svg";
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { mergeProfileIntoCachedUser } from "../../shared/_helpers/currentUser";
import { bookAppointment, createPayAsYouGoIntent, fetchSetupIntent, getProfile, purchaseSubscription, saveOneTimePayment, updateSubscriptionPaymentMethod } from "../../shared/_services/common.services";
import { bookingRefusalMessage } from "../../shared/_helpers/meetingEntitlement";
import {
  PAYMENT_UNCONFIRMED_MESSAGE,
  isPaymentConfirmed,
  isUnauthorized,
  paymentRefusalMessage,
  rememberUnconfirmedPayment,
} from "../../shared/_helpers/paymentErrors";
import { useSnackbar } from 'notistack';

// Initialize Stripe with your publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

function getCurrentUserFromStorage() {
  return JSON.parse(localStorage.getItem("currentUser"));
}

function buildTransactionDetails(paymentIntent) {
  return {
    ...paymentIntent,
    transactionId: paymentIntent.id,
  };
}

function resolveSubscriptionSuccessRoute(loginType, fromPage) {
  if (loginType === "mobile") return "/confirm-payment";
  if (fromPage === "changePlan" || fromPage === "login-canclledPlan") return "/profile";
  return "/confirm-payment";
}

// Exported for tests: the branch that used to sit at the `purchaseSubscription`
// call below is the substantive part of this screen, and reaching it through a
// render means standing up Stripe Elements for a confirmed PaymentIntent.
export async function handleSubscriptionPurchaseFlow({
  paymentIntent,
  priceId,
  fromPage,
  navigate,
  notify,
}) {
  const currentUser = getCurrentUserFromStorage();
  const loginType = currentUser?.user?.loginType;
  const email = currentUser?.user?.email;
  const transactionDetails = buildTransactionDetails(paymentIntent);

  // No `setupFee`. Backend SEC-110 made the server derive the amount from the
  // platform settings singleton (`quoteSetupFeeCents`) and stop reading the
  // client's number — it was sourced from `localStorage`, so the customer chose
  // what their own setup fee was. The key is still accepted and ignored, which
  // is exactly why it has to go from here: a field the server discards is a
  // number this screen only looks like it controls.
  const purchasePayload = {
    priceId,
    email,
    transactionDetails,
  };

  // One call for every login type. `loginType === "mobile"` used to divert to
  // `purchaseSubscriptionIOS`, which posted to `/subscription/purchase-subscription-ios`
  // with no `Authorization` header. Backend SEC-039 made the handler bill the
  // token subject instead of the body's `email`, so the ungated route could no
  // longer identify its caller, and SEC-003 (Astra-Backend#224) removed it —
  // that URL is a 404 now. The surviving route is the same handler taking the
  // same body; the missing token was the only difference between the pair.
  const purchaseSub = await purchaseSubscription(purchasePayload);

  // #88: `purchaseSubscription` resolves to the AxiosError on a non-2xx, which
  // is truthy, so the old `if (!purchaseSub) return` never fired and a refused
  // purchase (409 "already subscribed", 409 "payment already used") went on to
  // the success screen after Stripe had charged. Only a 200 is a purchase.
  if (!isPaymentConfirmed(purchaseSub)) {
    if (isUnauthorized(purchaseSub)) rememberUnconfirmedPayment();
    notify?.(paymentRefusalMessage(purchaseSub));
    return { purchased: false };
  }

  // A profile re-read, where a second write used to be. This called
  // `updateSelectedPlan` — `POST /users/update-selected-plan` — to set
  // `selected_plan` and the meeting allotment from a `purchasedSubscription` blob
  // the client echoed back, on an unauthenticated route that took the `userId`
  // from the body. Backend SEC-048 (Astra-Backend#52) removed it: the purchase
  // handler already has the subscription Stripe created and the plan beside it,
  // so it performs that write itself and the route is a 404 now.
  //
  // The write is not what this screen needed from the call, though — it needed
  // the *response*, which it merged into the cached user so the dashboard showed
  // the new plan and meeting count instead of the pre-purchase ones. Nothing tells
  // the client about a write the server made on its own, so the profile is
  // re-read here to get it. Dropping this entirely would leave the dashboard
  // showing a stale plan until the next full profile fetch.
  //
  // Merged rather than assigned wholesale: `GET /users/:userId` is projected to
  // the public allowlist (SEC-058/SEC-104), so it is a subset of the cached
  // object, and replacing would drop keys this app wrote locally.
  const profileRes = await getProfile(currentUser?.user?._id);

  if (profileRes?.status === 200) {
    // Merged into the cache as it is now, not the copy read before the await:
    // the silent token refresh may have rotated the token meanwhile.
    mergeProfileIntoCachedUser(profileRes.data?.data?.user, currentUser?.user?._id);
  }

  navigate(resolveSubscriptionSuccessRoute(loginType, fromPage));
  return { purchased: true };
}

// Exported for tests: the refused-booking path below cannot be reached through a
// render, because a real purchase cannot complete locally — `REACT_APP_STRIPE_PUBLIC_KEY`
// names a different Stripe account than the seeded price ids, so the card form
// never mounts (LOCAL_SETUP.md, Astra-Backend#248).
//
// Returns `{ booked }` so the caller can tell a refusal from a success. It used to
// return nothing and `return` silently on any non-200, which is the whole defect
// here: the customer had already been charged and credited by that point, so a
// refused booking left them paid-up with no appointment and no message, and then
// the caller navigated them to the confirmation screen anyway.
export async function handleOneTimePaymentFlow({
  paymentIntent,
  duration,
  navigate,
  notify,
  isPayAsYouGo = false,
}) {
  const email = getCurrentUserFromStorage()?.user?.email;
  const pendingAppointment = JSON.parse(localStorage.getItem("pendingAppointment"));
  const transactionDetails = buildTransactionDetails(paymentIntent);

  const oneTimeRes = await saveOneTimePayment(email, transactionDetails, duration, isPayAsYouGo);
  // #88: same `return error` trap as the subscription path. A refused credit
  // must not go on to book an appointment the account has no credit for.
  if (!isPaymentConfirmed(oneTimeRes)) {
    if (isUnauthorized(oneTimeRes)) rememberUnconfirmedPayment();
    notify?.(paymentRefusalMessage(oneTimeRes));
    return { booked: false };
  }

  const appointmentRes = await bookAppointment(pendingAppointment);

  // Backend ARCH-036 made `add-appointment` enforce the balance server-side: 403
  // with `{ message, meetings_required, meetings_available }` when it cannot cover
  // the booking, 409 when a concurrent update to the balance collides. The 409 is
  // plausible precisely here, because this booking races the credit the payment
  // just wrote. `bookAppointment` resolves rather than throws, so both arrive as an
  // axios error whose status is on `.response` — see `bookingRefusalMessage`.
  //
  // `pendingAppointment` is deliberately left in localStorage on a refusal: it is
  // what a retry replays, and the credit is already on the account, so rebooking
  // spends it rather than charging again.
  const unauthorized = isUnauthorized(appointmentRes);
  const refusal = unauthorized ? PAYMENT_UNCONFIRMED_MESSAGE : bookingRefusalMessage(appointmentRes);
  if (refusal) {
    if (unauthorized) rememberUnconfirmedPayment();
    notify?.(refusal);
    return { booked: false };
  }

  window.localStorage.setItem("bookedAppointment", JSON.stringify(appointmentRes?.data));
  window.localStorage.removeItem("pendingAppointment");
  navigate(isPayAsYouGo ? "/meetings" : "/confirm-payment");
  return { booked: true };
}

const SubscriptionForm = ({ type, priceId, duration }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [showPaymentLoader, setShowPaymentLoader] = useState(false)
  const location = useLocation();

  const fromPage = location.state?.from;

  // No setup-fee state and no `getSetupFee()` on mount. The fee was never
  // rendered here — it was fetched only to be forwarded on the purchase call the
  // server now ignores — so with that send gone the whole chain is dead. Note
  // the amount was read through `parseInt(setup_fee, 10)`, which turned "99.50"
  // into 99; don't reintroduce that if a fee is ever displayed on this screen.

  // A payment or booking refusal has to outlive the flow that produced it, so it
  // is a snackbar rather than inline text: the flows no longer navigate on a
  // refusal (#88), and this is the only thing the customer sees.
  const showPaymentError = (message) =>
    enqueueSnackbar(message, {
      variant: 'error',
      anchorOrigin: { horizontal: 'right', vertical: 'top' },
    });

  const handleSubmit = async (event) => {

    event.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    setShowPaymentLoader(true);
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: process.env.REACT_APP_URL },
        redirect: 'if_required',
      });
      if (error) {
        setShowPaymentLoader(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        const flowHandlers = {
          subscriptionPurchase: () =>
            handleSubscriptionPurchaseFlow({
              paymentIntent,
              priceId,
              fromPage,
              navigate,
              notify: showPaymentError,
            }),
          AddOn: () =>
            handleOneTimePaymentFlow({
              paymentIntent,
              duration,
              navigate,
              notify: showPaymentError,
              isPayAsYouGo: false,
            }),
          PayAsYouGo: () =>
            handleOneTimePaymentFlow({
              paymentIntent,
              duration,
              navigate,
              notify: showPaymentError,
              isPayAsYouGo: true,
            }),
        };

        const runFlow = flowHandlers[type];
        if (runFlow) {
          await runFlow();
        }
      }
      else {
        throw new Error("Payment Error!");
      }
    } catch (err) {
      console.warn(err);
    }
    setShowPaymentLoader(false);
    if (type === 'newCard') {
      navigate('/profile');
    }
    // No `navigate('/confirm-payment')` here for AddOn: `handleOneTimePaymentFlow`
    // navigates on a confirmed booking, and a thrown flow or an unsucceeded
    // PaymentIntent must never reach the confirmation screen (#88).
  };

  return (
    <form onSubmit={handleSubmit}>

      <>
        <PaymentElement />
        {(type === 'subscriptionPurchase' || type === 'AddOn' || type === 'PayAsYouGo') ? (
          <div className="text-center mt-5">
            {showPaymentLoader ? <button
              type="submit" className="as-btn-primary w-50"
              disabled={true}
            >
              Please wait...
            </button> :
              <button
                type="submit" className="as-btn-primary w-50"
                disabled={!stripe || !elements}
              >
              Yes, Connect with Plaid
              </button>}
          </div>
        ) : (
          <div className="text-center">
            <button
              type="submit" className="as-btn-primary w-50"
              disabled={!stripe || !elements}
            >
              Add New Card
            </button>
          </div>
        )}
        
      </>

    </form>
  );
};

// This is for addin New card
const PaymentForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  
  const [paymentMethodId, setPaymentMethodId] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const email = JSON.parse(localStorage.getItem('currentUser'))?.user?.email;

  const cardPaymentOptions = {
    style: {
      base: {
        color: '#32325d',
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '18px',
        padding: '10px 12px',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a',
      },
    },
  };

  const handleSubmit = async (e) => {

    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardElement);

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
    });

    if (error) {
      
      enqueueSnackbar(error.message, {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
    } else {
      setPaymentMethodId(paymentMethod.id);
      
    }

    if (paymentMethod?.id) {
      await updateSubscriptionPaymentMethod(email, paymentMethodId ? paymentMethodId : paymentMethod.id);
      
      enqueueSnackbar("New card added successfully.", {
        variant: 'success',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      navigate('/profile')
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <CardElement options={cardPaymentOptions} />
      <button type="submit" disabled={!stripe} className="as-btn-primary w-50 mt-5">
        Create Payment Method
      </button>
     
    </form>
  );
};

const MakePayment = () => {
  const [clientSecret, setClientSecret] = useState(null);
  const [duration, setDuration] = useState();
  const { cardContentType } = useSelector((state) => state?.stripeDetails?.data);
  const navigate = useNavigate();
  const location = useLocation();
  let addonMeeting = {}
  // No setup-fee state here, and none in `SubscriptionForm` either: nothing on
  // this screen reads the fee, and the server derives it (SEC-110), so neither
  // component fetches it any more.

  addonMeeting = location?.state?.from == "addtionalMeeting" ? location.state : "";
 



  window.onload = () => {
    if (cardContentType === 'subscriptionPurchase') {
      let result = clearSelectedPlan();
      if (result) navigate('/package');
    }
    else navigate('/profile');
  };
  useEffect(() => {

    const fetchData = async () => {
      
      let response;
      // No `loginType === 'mobile'` branch here either. It called
      // `fetchSetupIntentIOS`, whose route backend SEC-003 (Astra-Backend#224)
      // removed along with the purchase one. It was doubly broken besides: it
      // never assigned `response` or set `clientSecret`, so the Stripe form was
      // never rendered, and it read the amount from `selected_plan.unit_amount`
      // where Package.jsx writes a plan carrying `amount`.
      // ✅ Handle PayAsYouGo case
      if (cardContentType === "PayAsYouGo") {
        // No `amount`: the server prices the meeting from `duration` against the
        // platform singleton (SEC-048), and the value this screen used to send
        // arrived through `navigate` state from AppointmentBooking.jsx, which is
        // to say from the browser.
        response = await createPayAsYouGoIntent(
          addonMeeting?.email,
          addonMeeting?.duration
        );
        
        
        setClientSecret(response?.data?.clientSecret);
        setDuration(addonMeeting.duration); // or dynamically from backend
      }

      else {
        // No `setupFee` key. It was read from `selected_plan.setupFeePrice`, a
        // per-plan field no screen writes any more (#39), and the server adds
        // the platform fee to the intent total itself (SEC-110).
        // `priceId` is what the server prices the charge from (SEC-145):
        // Stripe's `unit_amount` for that price plus the setup fee. It is sent
        // only when there is a real id. BE-1 accepts and ignores it (expand
        // step); BE-2 requires it. `amount` is still sent because BE-1 servers
        // price from it; BE-2 ignores it.
        const cachedUser = JSON.parse(localStorage.getItem('currentUser'))?.user;
        const priceId = cachedUser?.selected_plan?.stripePriceId;
        response = await fetchSetupIntent({
          ...(priceId ? { priceId } : {}),
          amount: cachedUser?.selected_plan?.amount,
          email: cachedUser?.email,
        });
        setClientSecret(response?.data?.data?.client_secret);
      }
    };

    if (
      (cardContentType === 'newCard' ||
        cardContentType === 'subscriptionPurchase' || cardContentType === 'PayAsYouGo')
    ) {
      fetchData();
    }
    else if (cardContentType == 'AddOn') {
      
      setClientSecret(addonMeeting?.clientSecret)
      setDuration(addonMeeting?.duration)
    }

  }, [cardContentType, addonMeeting]);

  // Discards the plan the user picked on /package, so reloading this screen sends
  // them back to choose again rather than paying for a selection they can no
  // longer see.
  //
  // This deleted `currentUser.user.stripe_details.selectedPlan` and threw a
  // TypeError doing it: backend SEC-058 removed `stripe_details` from every user
  // payload (#53) and there is no optional chaining here, so
  // `delete undefined.selectedPlan` is a hard error.
  //
  // `selected_plan` is the right key regardless of the projection, and this is the
  // second half of the bug: `Package.jsx` writes the pending choice to
  // `currentUser.user.selected_plan` and its own `clearSelectedPlan` deletes that
  // key. This one was clearing a different field, so even before the projection
  // landed it left the stale selection in place while appearing to clear it.
  const clearSelectedPlan = () => {
    let currentUser = JSON.parse(localStorage.getItem('currentUser'));
    if (currentUser?.user?.selected_plan) {
      delete currentUser.user.selected_plan;
      localStorage.setItem('currentUser', JSON.stringify(currentUser));
    }
    return true;
  }


  



  return (
    <div className="package-payment-wrap payment-page">
      <div className="payment-logo text-center">
        <img className="package-payment-logo" src={MainLogo} alt="" />
      </div>
      <div className="container">
        <div className="payment-box add-card-box mt-5">

          {clientSecret &&
            <div className="text-center mt-5">
              {cardContentType === 'newCard' && (
                <Elements
                  stripe={stripePromise}
                  options={{ clientSecret }}
                >
                  <SubscriptionForm
                    type={cardContentType}
                  />
                </Elements>
              )}

              {cardContentType === 'subscriptionPurchase' && (
                <Elements
                  stripe={stripePromise}
                  options={{ clientSecret }}
                >
                  <SubscriptionForm
                    type='subscriptionPurchase'
                    priceId={JSON.parse(localStorage.getItem('currentUser'))?.user?.selected_plan?.stripePriceId}
                    clientSecret
                  />
                </Elements>
              )}

              {clientSecret && cardContentType === 'PayAsYouGo' && (
                <Elements
                  stripe={stripePromise}
                  options={{ clientSecret }}
                >
                  <SubscriptionForm
                    type='PayAsYouGo'
                    priceId="0"
                    duration={duration}
                  />
                </Elements>
              )}

              {cardContentType === 'AddOn' && (
                <Elements
                  stripe={stripePromise}
                  options={{ clientSecret }}
                >
                  <SubscriptionForm
                    type='AddOn'
                    priceId={JSON.parse(localStorage.getItem('currentUser'))?.user?.selected_plan?.stripePriceId}
                    duration={duration}
                  />
                </Elements>
              )}
            </div>}

          {cardContentType == 'AddNewCard' &&
            <div className="text-center mt-5">
              <Elements stripe={stripePromise}>
                <PaymentForm />
              </Elements>
            </div>

          }
        </div>
      </div>
    </div>
  );
};

export default MakePayment;
